/**
 * Full-page gate for logged-in recruiters on explorer-only surfaces.
 */
import React from 'react';
import { Briefcase, Sparkles, ClipboardList, Wand2, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RECRUITER_DEMO_URL } from '../constants/recruiterContact';
import { RecruiterGateCard } from './Recruiter/RecruiterGateCard';

interface RecruiterExplorerGateProps {
  children?: React.ReactNode;
}

const FeatureLine: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <li className="flex items-start gap-2.5 text-[13px] text-slate-700 leading-snug">
    <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-full bg-pink-50 text-pink-500 ring-1 ring-pink-100 shrink-0">
      {icon}
    </span>
    <span>{children}</span>
  </li>
);

const RecruiterExplorerGate: React.FC<RecruiterExplorerGateProps> = ({ children }) => {
  const { t } = useTranslation('hiringBoard');

  return (
    <div className="relative min-h-[60vh]">
      {children && (
        <div className="pointer-events-none select-none opacity-50 blur-[2px]" aria-hidden>
          {children}
        </div>
      )}

      <div className="absolute inset-0 z-10 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          <RecruiterGateCard
            icon={<Briefcase className="size-6 text-white" />}
            title={t('explorer_gate.title')}
            description={t('explorer_gate.description')}
          >
            <ul className="mt-7 space-y-3.5 text-left">
              <FeatureLine icon={<Sparkles className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_sourcing')}</FeatureLine>
              <FeatureLine icon={<ClipboardList className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_invite')}</FeatureLine>
              <FeatureLine icon={<Wand2 className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_formats')}</FeatureLine>
              <FeatureLine icon={<FileText className="w-3.5 h-3.5" />}>{t('explorer_gate.feature_analysis')}</FeatureLine>
            </ul>
            <a
              href={RECRUITER_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 py-3 text-sm font-semibold text-white shadow-lg shadow-pink-500/20 transition-all hover:shadow-xl hover:shadow-pink-500/25"
            >
              {t('explorer_gate.cta')}
            </a>
          </RecruiterGateCard>
        </div>
      </div>
    </div>
  );
};

export default RecruiterExplorerGate;
